import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const FEEDBACK_EMAIL = "geneo@afrogeneo.com";
const MAX_LEN = 400;

/**
 * Floating "Beta feedback" chip — bottom-right, app-wide.
 * Click opens a compact composer with a 1-line-ish input, submits to POST /api/feedback
 * AND opens a pre-filled mailto so the user can send too if they want.
 */
export default function FeedbackChip() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = async () => {
    const value = msg.trim();
    if (value.length < 3) { toast.error("Share a few more words."); return; }
    setSending(true);
    try {
      await api.post("/feedback", { message: value, path: location.pathname });
      toast.success("Thanks — we read every note.");
      // Also open a pre-filled mailto so the user can send the same message from their own inbox if they wish.
      const subject = encodeURIComponent("Geneo AI · Beta feedback");
      const body = encodeURIComponent(`${value}\n\n— sent from ${window.location.origin}${location.pathname}`);
      window.open(`mailto:${FEEDBACK_EMAIL}?subject=${subject}&body=${body}`, "_blank", "noopener");
      setMsg("");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="feedback-chip"
          aria-label="Send beta feedback"
          className="fixed bottom-5 right-5 z-40 px-4 py-2.5 rounded-full bg-[#0A0A0A]/90 backdrop-blur-md border border-[#F2C94C]/40 hover:border-[#F2C94C] hover:bg-[#F2C94C]/10 text-[#F2C94C] shadow-[0_10px_30px_-8px_rgba(242,201,76,0.4)] transition-all flex items-center gap-2 group"
        >
          <MessageSquare className="w-4 h-4 group-hover:rotate-[-8deg] transition-transform" />
          <span className="font-mono-hy text-[10px] tracking-widest">BETA FEEDBACK</span>
        </button>
      )}
      {open && (
        <div
          data-testid="feedback-panel"
          className="fixed bottom-5 right-5 z-40 w-[340px] max-w-[calc(100vw-24px)] rounded-2xl bg-[#120E15] border border-[#F2C94C]/40 shadow-[0_20px_60px_-10px_rgba(0,0,0,0.75)] overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-[#F2C94C]/15 flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#F2C94C] to-[#00E5FF] flex items-center justify-center">
              <MessageSquare className="w-3.5 h-3.5 text-[#0A0A0A]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono-hy text-[10px] text-[#F2C94C]">BETA FEEDBACK</div>
              <div className="font-mono-hy text-[9px] text-white truncate">→ {FEEDBACK_EMAIL}</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="feedback-close"
              aria-label="Close feedback panel"
              className="w-6 h-6 rounded-full text-white hover:text-[#E27D60] hover:bg-[#E27D60]/10 flex items-center justify-center"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value.slice(0, MAX_LEN))}
              placeholder="What's on your mind? Bugs, ideas, praise…"
              rows={3}
              autoFocus
              data-testid="feedback-input"
              className="w-full min-h-[80px] px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/20 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF]/40 focus:outline-none font-body text-sm text-white placeholder:text-white resize-none"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono-hy text-[9px] text-white tabular-nums">
                {msg.length}/{MAX_LEN}
              </span>
              <button
                onClick={submit}
                disabled={sending || msg.trim().length < 3}
                data-testid="feedback-send"
                className="font-mono-hy text-[10px] px-4 py-2 rounded-full bg-gradient-to-r from-[#F2C94C] to-[#00E5FF] text-[#0A0A0A] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                {sending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</> : <><Send className="w-3.5 h-3.5" /> Send</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
